import { Checkbox } from '@/components/ui/checkbox';
import { AssigneeStack } from './assignee-stack';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { cn } from '@/lib/utils';
import type { AllIssueBoardRef, TaskItem, TaskPriority, TaskStatusItem, TaskType } from '@hygge/shared';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Bookmark,
  Bug,
  Circle,
  MessageSquare,
  Minus,
  Star,
} from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { formatDate } from '@/lib/date';

/** A task row optionally carrying its owning board (for the cross-board list). */
type ListViewTask = TaskItem & { board?: AllIssueBoardRef };

interface ListViewProps {
  tasks: ListViewTask[];
  statuses: TaskStatusItem[];
  onTaskSelect?: (key: string) => void;
  selectionMode?: boolean;
  selectedKeys?: Set<string>;
  onToggleSelect?: (key: string) => void;
  /** Render a leading Board column (used by the cross-board "All issues" view). */
  showBoard?: boolean;
}

type SortField = 'key' | 'type' | 'title' | 'status' | 'dueDate' | 'assignee' | 'createdAt';
type SortDir = 'asc' | 'desc';

const priorityConfig: Record<TaskPriority, { icon: typeof ArrowUp; className: string }> = {
  urgent: { icon: ArrowUp, className: 'text-red-500' },
  high: { icon: ArrowUp, className: 'text-orange-500' },
  medium: { icon: Minus, className: 'text-yellow-500' },
  low: { icon: ArrowDown, className: 'text-blue-400' },
  none: { icon: Minus, className: 'text-muted-foreground/40' },
};

const typeConfig: Record<TaskType, { icon: typeof Circle; className: string }> = {
  task: { icon: Circle, className: 'text-blue-500' },
  bug: { icon: Bug, className: 'text-red-500' },
  story: { icon: Bookmark, className: 'text-violet-500' },
  epic: { icon: Star, className: 'text-amber-500' },
};

/**
 * What the rows can be piled into.
 *
 * Sorting answers "where is this one"; grouping answers "how much of this is
 * there" — a flat list of fifty-five rows can be ordered by status without ever
 * telling you that nine of them are in review. They are different questions and
 * the list could only answer the first.
 */
type GroupBy = 'none' | 'status' | 'assignee' | 'priority' | 'type';

const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'none', label: 'No grouping' },
  { value: 'status', label: 'Status' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'priority', label: 'Priority' },
  { value: 'type', label: 'Type' },
];

/** Worst first — a priority grouping ordered alphabetically would be useless. */
const PRIORITY_ORDER: TaskPriority[] = ['urgent', 'high', 'medium', 'low', 'none'];

interface Group {
  key: string;
  label: string;
  /** Drawn as a dot beside the label when the grouping has a colour to give. */
  color?: string;
  tasks: ListViewTask[];
}

export function ListView({
  tasks,
  statuses,
  onTaskSelect,
  selectionMode,
  selectedKeys,
  onToggleSelect,
  showBoard,
}: ListViewProps) {
  const formatName = useEmployeeNameFormatter();
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const statusMap = useMemo(() => {
    const m = new Map<string, TaskStatusItem>();
    for (const s of statuses) m.set(s.id, s);
    return m;
  }, [statuses]);

  const sorted = useMemo(() => {
    const list = [...tasks];
    const assigneeKey = (t: (typeof list)[number]) =>
      t.assignee ? formatName({ firstName: t.assignee.firstName, lastName: t.assignee.lastName }).toLowerCase() : 'zzz';
    list.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'key': {
          const aNum = parseInt(a.key.split('-')[1] ?? '0');
          const bNum = parseInt(b.key.split('-')[1] ?? '0');
          cmp = aNum - bNum;
          break;
        }
        case 'title':
          cmp = a.title.localeCompare(b.title);
          break;
        case 'status': {
          const aS = statusMap.get(a.statusId);
          const bS = statusMap.get(b.statusId);
          cmp = (aS?.sortOrder ?? 0) - (bS?.sortOrder ?? 0);
          break;
        }
        case 'dueDate': {
          const aDate = a.dueDate ? new Date(a.dueDate).getTime() : 0;
          const bDate = b.dueDate ? new Date(b.dueDate).getTime() : 0;
          cmp = aDate - bDate;
          break;
        }
        case 'assignee':
          // assigneeKey reads the PRIMARY assignee (task.assignee, mirrors
          // assignees[0]) through the name-display formatter (HW-730), so sorting
          // follows the configured order. Secondary assignees don't affect order.
          cmp = assigneeKey(a).localeCompare(assigneeKey(b));
          break;
        case 'type':
          cmp = a.type.localeCompare(b.type);
          break;
        case 'createdAt':
          cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [tasks, sortField, sortDir, statusMap, formatName]);

  /**
   * Rows piled by the chosen key, in an order that means something: statuses by
   * their board position, priorities worst-first, people alphabetically with
   * nobody last. Alphabetical statuses would put "Done" above "In progress",
   * which is the opposite of how a board is read.
   */
  const groups = useMemo<Group[]>(() => {
    if (groupBy === 'none') return [{ key: 'all', label: '', tasks: sorted }];

    const buckets = new Map<string, Group>();
    const put = (key: string, label: string, color: string | undefined, task: ListViewTask) => {
      const existing = buckets.get(key);
      if (existing) existing.tasks.push(task);
      else buckets.set(key, { key, label, color, tasks: [task] });
    };

    for (const task of sorted) {
      switch (groupBy) {
        case 'status': {
          const status = statusMap.get(task.statusId);
          put(task.statusId, status?.name ?? 'Unknown status', status?.color, task);
          break;
        }
        case 'assignee': {
          // The primary assignee, matching how the column and the sort read it.
          // A task with several assignees appears once, under the first.
          const a = task.assignee;
          put(
            a?.id ?? 'unassigned',
            a ? formatName({ firstName: a.firstName, lastName: a.lastName }) : 'Unassigned',
            undefined,
            task,
          );
          break;
        }
        case 'priority':
          put(task.priority, task.priority === 'none' ? 'No priority' : task.priority, undefined, task);
          break;
        case 'type':
          put(task.type, task.type, undefined, task);
          break;
      }
    }

    const list = [...buckets.values()];
    if (groupBy === 'status') {
      list.sort(
        (a, b) => (statusMap.get(a.key)?.sortOrder ?? 0) - (statusMap.get(b.key)?.sortOrder ?? 0),
      );
    } else if (groupBy === 'priority') {
      list.sort(
        (a, b) =>
          PRIORITY_ORDER.indexOf(a.key as TaskPriority) -
          PRIORITY_ORDER.indexOf(b.key as TaskPriority),
      );
    } else {
      // Nobody sorts last: an empty seat is not a person, and putting
      // "Unassigned" among the U's hides the pile that usually needs a decision.
      list.sort((a, b) => {
        if (a.key === 'unassigned') return 1;
        if (b.key === 'unassigned') return -1;
        return a.label.localeCompare(b.label);
      });
    }
    return list;
  }, [sorted, groupBy, statusMap, formatName]);

  // Select-all operates over the currently visible (sorted) rows only.
  const visibleKeys = useMemo(() => sorted.map((t) => t.key), [sorted]);
  const allSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selectedKeys?.has(k));
  const someSelected = visibleKeys.some((k) => selectedKeys?.has(k));
  const headerState: boolean | 'indeterminate' = allSelected
    ? true
    : someSelected
      ? 'indeterminate'
      : false;

  const toggleAll = () => {
    // Only flip the keys that need changing so a single toggle handler suffices.
    if (allSelected) {
      visibleKeys.forEach((k) => selectedKeys?.has(k) && onToggleSelect?.(k));
    } else {
      visibleKeys.forEach((k) => !selectedKeys?.has(k) && onToggleSelect?.(k));
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-0.5 text-foreground" />
      : <ArrowDown className="h-3 w-3 ml-0.5 text-foreground" />;
  };

  if (tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        No tasks found
      </div>
    );
  }

  const columnCount =
    7 + (selectionMode ? 1 : 0) + (showBoard ? 1 : 0);

  return (
    <div className="space-y-2">
      {/* One control, named. Grouping is off by default because a flat list is
          what someone scanning for a key wants; it is one click away for
          everyone else. */}
      <div className="flex items-center justify-end gap-2">
        <label htmlFor="list-group-by" className="text-[11px] text-muted-foreground">
          Group by
        </label>
        <select
          id="list-group-by"
          value={groupBy}
          onChange={(e) => {
            setGroupBy(e.target.value as GroupBy);
            // Collapse state belongs to the grouping that produced it; keeping
            // it across a change would hide a group the reader never closed.
            setCollapsed(new Set());
          }}
          className="h-7 cursor-pointer rounded-md border border-border bg-card px-2 text-[12px] text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {GROUP_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-lg border border-border overflow-hidden bg-background">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30">
            {selectionMode && (
              <th className="w-8 px-3 py-2.5">
                <Checkbox
                  checked={headerState}
                  onCheckedChange={toggleAll}
                  aria-label="Select all visible tasks"
                />
              </th>
            )}
            {showBoard && (
              <th className="text-left font-medium text-muted-foreground px-4 py-2.5 text-xs w-40">
                Board
              </th>
            )}
            <Th field="key" current={sortField} onClick={toggleSort} className="w-28">
              Key <SortIcon field="key" />
            </Th>
            <Th field="type" current={sortField} onClick={toggleSort} className="w-24">
              Type <SortIcon field="type" />
            </Th>
            <Th field="title" current={sortField} onClick={toggleSort} className="min-w-[140px]">
              Title <SortIcon field="title" />
            </Th>
            <Th field="status" current={sortField} onClick={toggleSort} className="w-28">
              Status <SortIcon field="status" />
            </Th>
            <th className="text-left font-medium text-muted-foreground px-4 py-2.5 text-xs w-32">
              Tags
            </th>
            <Th field="dueDate" current={sortField} onClick={toggleSort} className="w-24">
              Due date <SortIcon field="dueDate" />
            </Th>
            <Th field="assignee" current={sortField} onClick={toggleSort} className="w-40">
              Assignee <SortIcon field="assignee" />
            </Th>
            <th className="text-left font-medium text-muted-foreground px-4 py-2.5 text-xs w-20">
              Estimation
            </th>
            <th className="text-right font-medium text-muted-foreground px-4 py-2.5 text-xs w-16">
              Comments
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <Fragment key={group.key}>
              {groupBy !== 'none' && (
                <tr className="border-b border-border bg-muted/40">
                  <td colSpan={columnCount} className="px-3 py-1.5">
                    <button
                      type="button"
                      onClick={() =>
                        setCollapsed((prev) => {
                          const next = new Set(prev);
                          if (next.has(group.key)) next.delete(group.key);
                          else next.add(group.key);
                          return next;
                        })
                      }
                      aria-expanded={!collapsed.has(group.key)}
                      className="flex w-full cursor-pointer items-center gap-1.5 text-left text-[12px] font-medium text-foreground"
                    >
                      {collapsed.has(group.key) ? (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      {group.color && (
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: group.color }}
                        />
                      )}
                      <span className="capitalize">{group.label}</span>
                      {/* The count is the reason to group at all. */}
                      <span className="tabular-nums text-muted-foreground">
                        {group.tasks.length}
                      </span>
                    </button>
                  </td>
                </tr>
              )}
              {!collapsed.has(group.key) &&
                group.tasks.map((task) => {
            const status = statusMap.get(task.statusId);
            const pri = priorityConfig[task.priority];
            const PriIcon = pri.icon;
            const tp = typeConfig[task.type];
            const TpIcon = tp.icon;

            return (
              <tr
                key={task.id}
                className={cn(
                  'border-b border-border hover:bg-muted/30 cursor-pointer transition-colors',
                  selectionMode && selectedKeys?.has(task.key) && 'bg-hygge/5',
                )}
                onClick={() =>
                  selectionMode ? onToggleSelect?.(task.key) : onTaskSelect?.(task.key)
                }
              >
                {selectionMode && (
                  <td className="w-8 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedKeys?.has(task.key) ?? false}
                      onCheckedChange={() => onToggleSelect?.(task.key)}
                      aria-label={`Select ${task.key}`}
                    />
                  </td>
                )}
                {showBoard && (
                  <td className="px-4 py-3">
                    {task.board ? (
                      <div className="flex items-center gap-1.5">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: task.board.color }}
                        />
                        <span className="text-xs truncate">{task.board.name}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <PriIcon className={cn('h-3.5 w-3.5 shrink-0', pri.className)} />
                    <span className="text-xs font-mono font-semibold text-hygge">{task.key}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <TpIcon className={cn('h-3.5 w-3.5 shrink-0', tp.className)} />
                    <span className="text-xs capitalize">{task.type}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground line-clamp-1">{task.title}</span>
                </td>
                <td className="px-4 py-3">
                  {status && (
                    <div className="flex items-center gap-1.5">
                      <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: status.color }} />
                      <span className="text-xs truncate">{status.name}</span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {task.tags && task.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {task.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-mono font-medium truncate max-w-[80px]"
                          style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                        >
                          {tag.name}
                        </span>
                      ))}
                      {task.tags.length > 3 && (
                        <span className="text-[10px] font-mono text-muted-foreground">+{task.tags.length - 3}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {task.dueDate ? (
                    <span className="text-xs text-muted-foreground">
                      {formatDate(task.dueDate, 'monthDay')}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {(() => {
                    const people = task.assignees ?? (task.assignee ? [task.assignee] : []);
                    if (people.length === 0) {
                      return <span className="text-xs text-muted-foreground">—</span>;
                    }
                    return (
                      <div className="flex items-center gap-1.5">
                        <AssigneeStack people={people} size="sm" />
                        <span className="text-xs truncate">
                          {people.length === 1
                            ? formatName({ firstName: people[0].firstName, lastName: people[0].lastName })
                            : `${people.length} assignees`}
                        </span>
                      </div>
                    );
                  })()}
                </td>
                <td className="px-4 py-3">
                  {task.storyPoints != null ? (
                    <span className="text-xs font-mono text-muted-foreground">{task.storyPoints}</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {task._count.comments > 0 ? (
                    <div className="flex items-center justify-end gap-0.5 text-muted-foreground">
                      <MessageSquare className="h-3 w-3" />
                      <span className="text-[10px] tabular-nums">{task._count.comments}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            );
                })}
            </Fragment>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function Th({
  field,
  current,
  onClick,
  className,
  children,
}: {
  field: SortField;
  current: SortField;
  onClick: (f: SortField) => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <th
      className={cn(
            'text-left font-medium text-muted-foreground px-4 py-2.5 text-xs select-none cursor-pointer hover:text-foreground transition-colors',
        current === field && 'text-foreground',
        className,
      )}
      onClick={() => onClick(field)}
    >
      <div className="flex items-center">
        {children}
      </div>
    </th>
  );
}
